import React from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Utensils,
  Laptop,
  Info,
  ExternalLink,
  ArrowRight,
  Phone,
  Mail,
  Clock,
  Calendar,
  HelpCircle,
  ShieldCheck,
  Sparkles,
  ChefHat,
  Download,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHero, Section, Card } from "@/components/Section";
import { BRAND, IMAGES } from "@/lib/site";

const PRONOTE_URL = "https://pronote.lpjw.fr"; // à adapter selon l'URL réelle

// Exemple de menus de la semaine — à connecter ultérieurement à une source (PDF, API, etc.)
const MENUS = [
  { jour: "Lundi", entree: "Crudités variées", plat: "Rôti de bœuf, gratin dauphinois", dessert: "Yaourt nature & fruit" },
  { jour: "Mardi", entree: "Velouté de courge", plat: "Filet de poisson, riz pilaf, légumes", dessert: "Mousse au chocolat" },
  { jour: "Mercredi", entree: "Salade de pâtes maison", plat: "Émincé de volaille, ratatouille", dessert: "Compote pomme-poire" },
  { jour: "Jeudi", entree: "Salade verte aux noix", plat: "Lasagnes maison, salade", dessert: "Fromage blanc & coulis" },
  { jour: "Vendredi", entree: "Carottes râpées", plat: "Poisson pané, purée maison", dessert: "Fruit frais" },
];

const SECTIONS = [
  { key: "pronote", label: "Pronote", icon: GraduationCap },
  { key: "self", label: "Restaurant scolaire", icon: Utensils },
  { key: "ordi", label: "MonOrdiAuLycée", icon: Laptop },
  { key: "infos", label: "Infos pratiques", icon: Info },
];

export default function ParentsEleves() {
  return (
    <div data-testid="parents-eleves-page">
      <PageHero
        eyebrow="Espace Parents & Élèves"
        title="Votre tableau de bord pour suivre la scolarité au quotidien"
        subtitle="Notes, emploi du temps, menus du self, prêt d'ordinateur, contacts utiles : tout ce qu'il vous faut pour rester en lien avec le lycée, en un seul endroit."
        image={IMAGES.classroom}
      />

      <Section>
        <Tabs defaultValue="pronote" className="w-full" data-testid="parents-tabs">
          {/* Tabs list — pills modernes responsives */}
          <TabsList className="mb-10 flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
            {SECTIONS.map((s) => (
              <TabsTrigger
                key={s.key}
                value={s.key}
                data-testid={`tab-${s.key}`}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-brand-cream px-5 py-3 text-sm font-semibold text-brand-slate-dark transition-all data-[state=active]:bg-brand-slate data-[state=active]:text-brand-cream data-[state=active]:shadow-md hover:-translate-y-0.5"
              >
                <s.icon className="h-4 w-4" />
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* PRONOTE */}
          <TabsContent value="pronote" data-testid="content-pronote">
            <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-start">
              <div className="rounded-3xl bg-brand-slate-dark p-8 text-brand-cream lg:p-12">
                <div className="inline-flex items-center gap-2 rounded-full bg-brand-sand/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-sand">
                  <Sparkles className="h-3.5 w-3.5" /> Service principal
                </div>
                <h2 className="mt-5 font-heading text-3xl font-bold sm:text-4xl">
                  Accédez à Pronote en un clic
                </h2>
                <p className="mt-4 text-base leading-relaxed text-brand-cream/85">
                  Pronote est votre plateforme officielle pour suivre toute la scolarité de votre
                  enfant : notes, devoirs, absences, emploi du temps, cahier de textes et messagerie
                  avec les enseignants.
                </p>
                <ul className="mt-6 grid gap-2 text-sm text-brand-cream/85 sm:grid-cols-2">
                  {[
                    "Notes & bulletins en temps réel",
                    "Emploi du temps mis à jour",
                    "Devoirs & cahier de textes",
                    "Absences & retards",
                    "Messagerie avec les profs",
                    "Vie scolaire & sanctions",
                  ].map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-sand" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={PRONOTE_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="pronote-cta-access"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
                  >
                    Accéder à Pronote
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <a
                    href="https://www.index-education.com/fr/pronote-mobile.php"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="pronote-cta-mobile"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-cream hover:text-brand-slate-dark"
                  >
                    Application mobile
                  </a>
                </div>
              </div>

              <Card data-testid="pronote-help">
                <HelpCircle className="h-7 w-7 text-brand-sage-dark" />
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-slate-dark">
                  Premier accès ou mot de passe oublié ?
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Vos identifiants vous ont été remis à la rentrée. En cas de perte, contactez la vie
                  scolaire qui vous renverra un nouveau code.
                </p>
                <a
                  href="mailto:viescolaire@lpjw.fr"
                  data-testid="pronote-contact-vs"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-sand-wrapper px-5 py-2.5 text-sm font-semibold text-brand-slate-dark transition-colors hover:bg-brand-sand"
                >
                  <Mail className="h-4 w-4" />
                  viescolaire@lpjw.fr
                </a>
              </Card>
            </div>
          </TabsContent>

          {/* RESTAURANT SCOLAIRE */}
          <TabsContent value="self" data-testid="content-self">
            <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-start">
              <div>
                <h2 className="font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
                  Le restaurant scolaire
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  Une cuisine 100 % maison, élaborée chaque jour par notre équipe de restauration.
                  Nous privilégions les produits frais, locaux et de saison pour offrir aux élèves
                  des repas équilibrés et savoureux.
                </p>
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  {[
                    { icon: ChefHat, t: "Cuisine maison", d: "Préparée sur place chaque jour" },
                    { icon: Sparkles, t: "Produits locaux", d: "Circuits courts du Maine-et-Loire" },
                    { icon: ShieldCheck, t: "Équilibre nutritionnel", d: "Menus validés par une diététicienne" },
                  ].map((c) => (
                    <div key={c.t} className="rounded-2xl border border-black/5 bg-brand-cream p-5">
                      <c.icon className="h-6 w-6 text-brand-sage-dark" />
                      <p className="mt-3 font-heading text-sm font-bold text-brand-slate-dark">{c.t}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{c.d}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="#menus-semaine"
                    data-testid="self-cta-menu"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
                  >
                    <Utensils className="h-4 w-4" />
                    Voir le menu du self
                  </a>
                  <a
                    href="/menus-semaine.pdf"
                    download
                    data-testid="self-cta-pdf"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
                  >
                    <Download className="h-4 w-4" />
                    Télécharger en PDF
                  </a>
                </div>
              </div>

              <Card>
                <Info className="h-7 w-7 text-brand-slate-dark" />
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-slate-dark">
                  Modalités d'inscription
                </h3>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />Demi-pension 3, 4 ou 5 jours / semaine</li>
                  <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />Repas occasionnel possible : 6,90 €</li>
                  <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />Choix défini à l'inscription, modifiable au trimestre</li>
                </ul>
                <Link
                  to="/infos-pratiques"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-slate transition-colors hover:text-brand-sand-dark"
                >
                  Voir les tarifs <ArrowRight className="h-4 w-4" />
                </Link>
              </Card>
            </div>

            {/* Menus de la semaine */}
            <div id="menus-semaine" className="mt-10">
              <h3 className="font-heading text-2xl font-bold text-brand-slate-dark">
                Menus de la semaine
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Menus à titre indicatif — susceptibles d'être ajustés selon les arrivages.
              </p>

              <div className="mt-6 overflow-hidden rounded-3xl border border-black/5 bg-brand-cream shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm" data-testid="menus-table">
                    <thead>
                      <tr className="bg-brand-sand-wrapper text-brand-slate-dark">
                        <th className="px-5 py-4 text-left font-heading font-semibold">Jour</th>
                        <th className="px-5 py-4 text-left font-heading font-semibold">Entrée</th>
                        <th className="px-5 py-4 text-left font-heading font-semibold">Plat</th>
                        <th className="px-5 py-4 text-left font-heading font-semibold">Dessert</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MENUS.map((m, i) => (
                        <tr key={m.jour} data-testid={`menu-${i}`} className="border-t border-black/5">
                          <td className="px-5 py-4 font-heading font-semibold text-brand-slate-dark">{m.jour}</td>
                          <td className="px-5 py-4 text-muted-foreground">{m.entree}</td>
                          <td className="px-5 py-4 text-muted-foreground">{m.plat}</td>
                          <td className="px-5 py-4 text-muted-foreground">{m.dessert}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* MONORDIAULYCEE */}
          <TabsContent value="ordi" data-testid="content-ordi">
            <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-start">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-brand-sand-wrapper px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-slate-dark">
                  <Sparkles className="h-3.5 w-3.5" /> Région Pays de la Loire
                </div>
                <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
                  MonOrdiAuLycée : un ordinateur pour chaque lycéen
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  La Région Pays de la Loire équipe chaque lycéen et apprenti d'un ordinateur portable
                  dans le cadre du dispositif <strong className="text-brand-slate-dark">MonOrdiAuLycée</strong>.
                  Le matériel est mis à disposition gratuitement par le lycée pour toute la scolarité.
                </p>

                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <div className="rounded-2xl border border-black/5 bg-brand-cream p-5">
                    <Laptop className="h-6 w-6 text-brand-slate-dark" />
                    <p className="mt-3 font-heading text-sm font-bold text-brand-slate-dark">
                      Remise du matériel
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      À la rentrée, lors d'une cérémonie de remise. Une attestation est signée par
                      l'élève et son responsable légal.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-black/5 bg-brand-cream p-5">
                    <ShieldCheck className="h-6 w-6 text-brand-sage-dark" />
                    <p className="mt-3 font-heading text-sm font-bold text-brand-slate-dark">
                      Conditions
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Être inscrit au lycée (Seconde, Première, Terminale ou apprentissage). Engagement
                      à respecter une charte de bon usage.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-black/5 bg-brand-cream p-5">
                    <HelpCircle className="h-6 w-6 text-brand-slate" />
                    <p className="mt-3 font-heading text-sm font-bold text-brand-slate-dark">
                      Assistance technique
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      En cas de panne ou de problème, un référent numérique du lycée vous accompagne et
                      prend contact avec le SAV régional.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-black/5 bg-brand-cream p-5">
                    <Calendar className="h-6 w-6 text-brand-sand-dark" />
                    <p className="mt-3 font-heading text-sm font-bold text-brand-slate-dark">
                      Restitution
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      À la fin du parcours au lycée. L'élève peut conserver son équipement selon les
                      modalités prévues par la Région.
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="https://www.paysdelaloire.fr/les-aides/monordiaulycee-pour-tous-les-lyceens-et-apprentis"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="ordi-cta-savoir"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
                  >
                    En savoir plus
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <a
                    href="mailto:numerique@lpjw.fr"
                    data-testid="ordi-cta-help"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
                  >
                    <Mail className="h-4 w-4" />
                    Référent numérique
                  </a>
                </div>
              </div>

              <Card className="bg-brand-sand-wrapper/60">
                <Info className="h-7 w-7 text-brand-slate-dark" />
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-slate-dark">
                  Bon à savoir
                </h3>
                <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                  {[
                    "L'ordinateur reste la propriété de la Région pendant la scolarité",
                    "En cas de perte ou de casse, prévenez immédiatement le lycée",
                    "Le matériel est couvert par une assurance via la Région",
                    "Une charte d'usage est signée à la remise",
                  ].map((it) => (
                    <li key={it} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />
                      {it}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </TabsContent>

          {/* INFOS PRATIQUES */}
          <TabsContent value="infos" data-testid="content-infos">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Horaires */}
              <Card>
                <Clock className="h-7 w-7 text-brand-slate-dark" />
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-slate-dark">
                  Horaires du lycée
                </h3>
                <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
                  <li className="flex justify-between gap-4 border-b border-black/5 pb-2">
                    <span className="font-medium text-brand-slate-dark">Lundi – vendredi</span>
                    <span>8h00 – 12h30 / 13h15 – 17h30</span>
                  </li>
                  <li className="flex justify-between gap-4 border-b border-black/5 pb-2">
                    <span className="font-medium text-brand-slate-dark">Mercredi après-midi</span>
                    <span>Cours possibles selon emploi du temps</span>
                  </li>
                  <li className="flex justify-between gap-4">
                    <span className="font-medium text-brand-slate-dark">Samedi</span>
                    <span>Sur rendez-vous uniquement</span>
                  </li>
                </ul>
              </Card>

              {/* Contacts */}
              <Card>
                <Phone className="h-7 w-7 text-brand-slate-dark" />
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-slate-dark">
                  Contacts utiles
                </h3>
                <ul className="mt-4 space-y-3 text-sm">
                  <li>
                    <p className="font-medium text-brand-slate-dark">Vie scolaire</p>
                    <a href="mailto:viescolaire@lpjw.fr" className="text-muted-foreground hover:text-brand-sand-dark">
                      viescolaire@lpjw.fr
                    </a>
                  </li>
                  <li>
                    <p className="font-medium text-brand-slate-dark">Secrétariat</p>
                    <a href={`tel:${BRAND.telephone.replace(/\s/g, "")}`} className="text-muted-foreground hover:text-brand-sand-dark">
                      {BRAND.telephone}
                    </a>{" "}
                    ·{" "}
                    <a href={`mailto:${BRAND.email}`} className="text-muted-foreground hover:text-brand-sand-dark">
                      {BRAND.email}
                    </a>
                  </li>
                  <li>
                    <p className="font-medium text-brand-slate-dark">Adjoint de gestion</p>
                    <a href="mailto:adjointdegestion@lpjw.fr" className="text-muted-foreground hover:text-brand-sand-dark">
                      adjointdegestion@lpjw.fr
                    </a>
                  </li>
                </ul>
              </Card>

              {/* Calendrier */}
              <Card>
                <Calendar className="h-7 w-7 text-brand-slate-dark" />
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-slate-dark">
                  Calendrier scolaire
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Téléchargez le calendrier complet de l'année 2025-2026 : vacances, conseils de
                  classe, examens, journées portes ouvertes.
                </p>
                <Link
                  to="/telechargements"
                  data-testid="infos-cta-calendrier"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-slate px-5 py-2.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-slate-dark"
                >
                  <Download className="h-4 w-4" />
                  Voir le calendrier
                </Link>
              </Card>

              {/* Aide / FAQ */}
              <Card>
                <HelpCircle className="h-7 w-7 text-brand-slate-dark" />
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-slate-dark">
                  Aide & FAQ
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Une question sur l'inscription, les frais, les transports, l'internat ? Consultez
                  notre foire aux questions sur la page d'accueil.
                </p>
                <Link
                  to="/#faq"
                  data-testid="infos-cta-faq"
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-5 py-2.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
                >
                  Voir la FAQ
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </Section>
    </div>
  );
}
